import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import comptabiliteService, { Immobilisation, PlanAmortissementLigne } from '../../api/comptabilite';
import { formatTND } from '../../utils/formatters';

const today = () => new Date().toISOString().slice(0, 10);

const Immobilisations: React.FC = () => {
  const [items, setItems] = useState<Immobilisation[]>([]);
  const [plan, setPlan] = useState<{ id: number; lignes: PlanAmortissementLigne[] } | null>(null);
  const [form, setForm] = useState({
    libelle: '', compteImmobilisation: '223', valeurAcquisition: '', valeurResiduelle: '0',
    dateAcquisition: today(), dureeMois: '60',
  });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      setItems(await comptabiliteService.listImmobilisations());
    } catch (e: any) {
      if (e?.response?.status !== 403) toast.error('Chargement impossible.');
    }
  };
  useEffect(() => { refresh(); }, []);

  const creer = async () => {
    setBusy(true);
    try {
      await comptabiliteService.createImmobilisation({
        libelle: form.libelle,
        compteImmobilisation: form.compteImmobilisation,
        dateAcquisition: form.dateAcquisition,
        valeurAcquisition: Number(form.valeurAcquisition),
        valeurResiduelle: Number(form.valeurResiduelle) || 0,
        dureeMois: Number(form.dureeMois),
      });
      toast.success('Immobilisation ajoutée.');
      setForm({ ...form, libelle: '', valeurAcquisition: '' });
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Création impossible.');
    } finally { setBusy(false); }
  };

  const voirPlan = async (i: Immobilisation) => {
    try {
      setPlan({ id: i.id, lignes: await comptabiliteService.planAmortissement(i.id) });
    } catch (e: any) {
      toast.error('Plan indisponible.');
    }
  };

  const supprimer = async (i: Immobilisation) => {
    if (!window.confirm(`Supprimer « ${i.libelle} » ?`)) return;
    try {
      await comptabiliteService.deleteImmobilisation(i.id);
      if (plan?.id === i.id) setPlan(null);
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Suppression impossible.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link to="/comptabilite" className="text-sm text-blue-600 hover:underline">← Comptabilité</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">Immobilisations &amp; amortissements</h1>
        <p className="text-sm text-gray-500">
          Registre des actifs et plan d'amortissement linéaire. Les dotations se génèrent depuis l'écran Exercices.
        </p>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Nouvelle immobilisation</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Libellé</label>
            <input value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })}
                   className="w-full rounded-lg border border-gray-200 px-3 py-2" placeholder="Matériel informatique" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Compte (21x/22x)</label>
            <input value={form.compteImmobilisation} onChange={(e) => setForm({ ...form, compteImmobilisation: e.target.value })}
                   className="w-full rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Valeur d'acquisition</label>
            <input type="number" value={form.valeurAcquisition} onChange={(e) => setForm({ ...form, valeurAcquisition: e.target.value })}
                   className="w-full rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Valeur résiduelle</label>
            <input type="number" value={form.valeurResiduelle} onChange={(e) => setForm({ ...form, valeurResiduelle: e.target.value })}
                   className="w-full rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Durée (mois)</label>
            <input type="number" value={form.dureeMois} onChange={(e) => setForm({ ...form, dureeMois: e.target.value })}
                   className="w-full rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Date d'acquisition</label>
            <input type="date" value={form.dateAcquisition} onChange={(e) => setForm({ ...form, dateAcquisition: e.target.value })}
                   className="w-full rounded-lg border border-gray-200 px-3 py-2" />
          </div>
        </div>
        <div className="mt-4">
          <button onClick={creer} disabled={busy || !form.libelle || !form.valeurAcquisition}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
            Ajouter
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left px-4 py-3">Libellé</th>
              <th className="text-left px-4 py-3">Compte</th>
              <th className="text-right px-4 py-3">Valeur</th>
              <th className="text-right px-4 py-3">Amorti</th>
              <th className="text-right px-4 py-3">VNC</th>
              <th className="text-center px-4 py-3">Durée</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-t border-gray-100">
                <td className="px-4 py-3 font-medium text-gray-900">{i.libelle}</td>
                <td className="px-4 py-3 text-gray-600">{i.compteImmobilisation}</td>
                <td className="px-4 py-3 text-right">{formatTND(i.valeurAcquisition)}</td>
                <td className="px-4 py-3 text-right text-gray-600">{formatTND(i.cumulAmortissements)}</td>
                <td className="px-4 py-3 text-right font-semibold">{formatTND(i.valeurNetteComptable)}</td>
                <td className="px-4 py-3 text-center text-gray-600">{i.dureeMois} m</td>
                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                  <button onClick={() => voirPlan(i)} className="text-blue-600 hover:underline">Plan</button>
                  <button onClick={() => supprimer(i)} className="text-red-600 hover:underline">Suppr.</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Aucune immobilisation.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {plan && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Plan d'amortissement</h3>
            <button onClick={() => setPlan(null)} className="text-sm text-gray-400 hover:text-gray-600">Fermer</button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left px-4 py-3">Année</th>
                <th className="text-right px-4 py-3">Dotation</th>
                <th className="text-right px-4 py-3">Cumul</th>
                <th className="text-right px-4 py-3">Valeur nette</th>
              </tr>
            </thead>
            <tbody>
              {plan.lignes.map((l) => (
                <tr key={l.annee} className="border-t border-gray-100">
                  <td className="px-4 py-2 text-gray-900">{l.annee}</td>
                  <td className="px-4 py-2 text-right">{formatTND(l.dotation)}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{formatTND(l.cumul)}</td>
                  <td className="px-4 py-2 text-right">{formatTND(l.valeurNette)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Immobilisations;
